import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { BM_ROLES, ADMIN_ROLES } from "@/lib/constants";
import Login from "@/pages/Login";
import SetPassword from "@/pages/SetPassword";
import Layout from "@/components/Layout";
import MapPage from "@/pages/MapPage";
import Apartments from "@/pages/Apartments";
import ApartmentDetail from "@/pages/ApartmentDetail";
import Problems from "@/pages/Problems";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import Leases from "@/pages/Leases";
import LeaseUpload from "@/pages/LeaseUpload";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Maintenance from "@/pages/Maintenance";
import TrackTour from "@/pages/TrackTour";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Chargement…</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function RequireRole({ roles, children }) {
  const { user } = useAuth();
  if (!user || !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google-session", {}, { headers: { "X-Session-ID": sid } });
        if (data.token) localStorage.setItem("token", data.token);
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true, state: { user: data } });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Connexion Google impossible");
        setUser(false);
        navigate("/login", { replace: true });
      }
    })();
  }, [location, navigate, setUser]);

  return <div className="min-h-screen flex items-center justify-center text-slate-500"
              data-testid="auth-callback">Connexion Google…</div>;
}

function AppRouter() {
  const location = useLocation();
  // Détection synchrone du retour OAuth (session_id dans le fragment) AVANT les routes protégées
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/bm-upload/:token" element={<LeaseUpload />} />
      <Route path="/confidentialite" element={<PrivacyPolicy />} />
      <Route path="/maintenance/suivi/:token" element={<TrackTour />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/" element={<MapPage />} />
        <Route path="/apartments" element={<Apartments />} />
        <Route path="/apartments/:id" element={<ApartmentDetail />} />
        <Route path="/problems" element={<Problems />} />
        <Route path="/maintenance" element={<RequireRole roles={BM_ROLES}><Maintenance /></RequireRole>} />
        <Route path="/baux" element={<RequireRole roles={BM_ROLES}><Leases /></RequireRole>} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/admin" element={<RequireRole roles={ADMIN_ROLES}><Admin /></RequireRole>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
